import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Bookmark, Award, Hash, Info, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface TrendingTopic {
  topic: string;
  post_count: number;
}

const Sidebar: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrendingTopics = async () => {
      try {
        setError(null);
        console.log('🔍 Fetching trending topics...');
        
        const { data, error: fetchError } = await supabase
          .from('trending_topics')
          .select('*')
          .order('post_count', { ascending: false })
          .limit(5);

        if (fetchError) {
          console.error('❌ Error fetching trending topics:', fetchError);
          throw fetchError;
        }

        console.log('✅ Trending topics data:', data);
        setTrendingTopics(data || []);
      } catch (err) {
        console.error('Error fetching trending topics:', err);
        setError('Failed to load trending topics');
        setTrendingTopics([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrendingTopics();

    // Set up real-time subscription
    const subscription = supabase
      .channel('trending_topics_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trending_topics'
        },
        () => {
          fetchTrendingTopics();
        }
      )
      .subscribe();

    // Refresh trending topics every 5 minutes
    const interval = setInterval(fetchTrendingTopics, 5 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (!currentUser) return null;

  return (
    <>
      <aside className="hidden md:flex flex-col w-64 border-r border-gray-800 pt-4 h-[calc(100vh-4rem)] fixed mt-16 backdrop-blur-md bg-[#272727] z-30">
        <div className="space-y-1 px-4">
          <Link 
            to="/home" 
            className={`flex items-center px-3 py-3 rounded-md transition-colors ${
              isActive('/home') 
                ? 'bg-[#404040] backdrop-blur-md text-white' 
                : 'text-gray-300 hover:bg-[#404040] hover:text-white'
            }`}
          >
            <Home className="h-5 w-5 mr-3" />
            <span>Home</span>
          </Link>
          
          <Link 
            to="/home/bookmarks" 
            className={`flex items-center px-3 py-3 rounded-md transition-colors ${
              isActive('/home/bookmarks') 
                ? 'bg-[#404040] backdrop-blur-md text-white' 
                : 'text-gray-300 hover:bg-[#404040] hover:text-white'
            }`}
          >
            <Bookmark className="h-5 w-5 mr-3" />
            <span>Saved Stories</span>
          </Link>
          
          <Link
            to="/home/verified"
            className={`flex items-center px-3 py-3 rounded-md transition-colors ${
              isActive('/home/verified')
                ? 'bg-[#404040] backdrop-blur-md text-white'
                : 'text-gray-300 hover:bg-[#404040] hover:text-white'
            }`}
          >
            <Award className="h-5 w-5 mr-3" />
            <span>Verified Journalists</span>
          </Link>

          {currentUser.is_admin && (
            <Link
              to="/home/admin"
              className={`flex items-center px-3 py-3 rounded-md transition-colors ${
                isActive('/home/admin')
                  ? 'bg-purple-500/20 backdrop-blur-md text-purple-400 border border-purple-500/50'
                  : 'text-gray-300 hover:bg-[#404040] hover:text-white'
              }`}
            >
              <Shield className="h-5 w-5 mr-3" />
              <span>Admin Dashboard</span>
            </Link>
          )}
        </div>
        
        <div className="mt-8 px-4">
          <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Trending Topics
          </h3>
          <div className="mt-2 space-y-1">
            {isLoading ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Loading topics...
              </div>
            ) : error ? (
              <div className="px-3 py-2 text-red-400 text-sm">
                {error}
                <button 
                  onClick={() => window.location.reload()} 
                  className="block mt-1 text-xs underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            ) : trendingTopics.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No trending topics yet
              </div>
            ) : (
              trendingTopics.map((topic) => (
                <Link 
                  key={topic.topic}
                  to={`/home/topic/${topic.topic.toLowerCase()}`}
                  className="flex items-center px-3 py-2 text-gray-300 hover:bg-[#404040] hover:text-white rounded-md text-sm group"
                >
                  <Hash className="h-4 w-4 mr-2 text-gray-500 group-hover:text-gray-300" />
                  <span className="truncate">{topic.topic}</span>
                  <span className="ml-auto text-xs text-gray-500 group-hover:text-gray-400 transition-colors">
                    {topic.post_count} {topic.post_count === 1 ? 'post' : 'posts'}
                  </span>
                </Link>
              ))
            )}
          </div>
        </div>

        <div className="mt-auto">
          <Link 
            to="/home/about"
            className={`flex items-center px-3 py-3 mx-4 text-gray-300 hover:bg-[#404040] hover:text-white transition-colors rounded-md ${
              isActive('/home/about') ? 'bg-[#404040] text-white' : ''
            }`}
          >
            <Info className="h-5 w-5 mr-3" />
            <span>About</span>
          </Link>
          
          <div className="p-4 border-t border-gray-800">
            <div className="flex items-center space-x-3">
              <img 
                src={currentUser.photoURL} 
                alt={currentUser.username}
                className="w-10 h-10 rounded-full object-cover border border-gray-700"
              />
              <div>
                <Link to={`/home/profile/${currentUser.username.toLowerCase()}`} className="font-medium text-white">
                  {currentUser.username}
                </Link>
                <p className="text-xs text-gray-400 truncate max-w-[10rem]">{currentUser.bio}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
      
      <div className="hidden md:block w-64"></div>
    </>
  );
};

export default Sidebar;